"use client";

import type { IconicVoiceItem } from "@/app/_api/elevenlabs/iconicVoices";

import { AudioProvider, useAudio } from "@/app/_providers/AudioProvider";
import { Button } from "@ui/components/core/Button";
import { ListTable, ListTableRow } from "@ui/components/core/ListTable";
import { PlayPauseButton } from "@ui/components/core/PlayPauseButton";
import { RequestVoiceDialog } from "@ui/components/core/RequestVoiceDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@ui/components/core/Tooltip";
import { ChevronRight, SearchIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import Play20 from "@ui/icons/20x20/play-20.svg";

import { Block } from "../Block";

interface IconicVoicesTableProps {
  searchPlaceholder?: null | string;
  title?: null | string;
  voices: IconicVoiceItem[];
}

const VoiceTableRow = ({ voice }: { voice: IconicVoiceItem }) => {
  const { playingId, play, stop } = useAudio();
  const t = useTranslations("ClientKeys.IconicVoicesTable");
  const isPlaying = playingId === voice.id;

  const handlePlayPause = () => {
    if (!voice.previewAudioUrl) return;

    if (isPlaying) {
      stop();
    } else {
      play(voice.id, voice.previewAudioUrl);
    }
  };

  return (
    <div className='tw-grid tw-grid-cols-2 tw-items-center tw-gap-4 tw-py-4 tw-transition-colors hover:tw-bg-white'>
      <div className='tw-flex tw-items-center tw-gap-3'>
        {voice.previewAudioUrl && !voice.playingDisabled ? (
          <PlayPauseButton
            className='tw-shrink-0'
            iconSize='sm'
            isPlaying={isPlaying}
            onClick={handlePlayPause}
            size='pill-md'
            variant='primary'
          />
        ) : (
          <TooltipProvider>
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <Button
                  aria-label={t("disabled_play")}
                  className='tw-size-8 tw-shrink-0 tw-opacity-80'
                  icon={<Play20 />}
                  disabled
                  size='pill-md'
                  variant='primary'
                />
              </TooltipTrigger>
              <TooltipContent className='tw-p-3'>
                <p className='tw-type-2xs'>{t("preview_coming_soon")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <div className='tw-min-w-0'>
          <p className='tw-type-base tw-text-black'>{voice.name}</p>
          {voice.shortBio && (
            <p className='tw-mt-2 tw-text-gray-500 tw-type-2xs'>
              <span className='tw-block tw-truncate'>{voice.shortBio}</span>
            </p>
          )}
        </div>
      </div>

      <div className='tw-self-stretch tw-flex tw-justify-end'>
        {voice.requestingDisabled ? (
          <div className='tw-w-full tw-flex tw-justify-end tw-items-center tw-px-4' />
        ) : (
          <a
            href={voice.requestUrl}
            className='tw-group tw-w-full tw-flex tw-justify-end tw-items-center tw-text-base/none tw-text-black tw-whitespace-nowrap tw-px-4 tw-outline-none tw-no-underline'
          >
            <div className='tw-flex tw-items-center tw-gap-x-3 group-focus-visible:tw-outline-focus tw-rounded group-focus-visible:tw-outline-offset-4'>
              {t("request_voice")}
              <ChevronRight size={16} className='tw-flex-none tw-hidden sm:tw-block' />
            </div>
          </a>
        )}
      </div>
    </div>
  );
};

const IconicVoicesTableLayout = ({ searchPlaceholder, title, voices }: IconicVoicesTableProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounceValue(searchQuery, 300);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const t = useTranslations("ClientKeys.IconicVoicesTable");

  const filteredVoices = useMemo(() => {
    const filtered = debouncedSearchQuery.trim()
      ? voices.filter(v => v.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()))
      : [...voices];

    return filtered.sort((a, b) => {
      const aHasPreview = !!a.previewAudioUrl;
      const bHasPreview = !!b.previewAudioUrl;

      if (aHasPreview && !bHasPreview) return -1;
      if (!aHasPreview && bHasPreview) return 1;

      return a.name.localeCompare(b.name);
    });
  }, [voices, debouncedSearchQuery]);

  if (!voices || voices.length === 0) return null;

  return (
    <Block name='iconic-voices-table'>
      <div className='tw-container'>
        <div className='tw-mb-8 tw-flex tw-flex-col tw-gap-6 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between sm:tw-mb-16'>
          {title && <h3 className='tw-type-6xl tw-text-black'>{title}</h3>}

          <div className='tw-max-w-80 tw-relative'>
            <div className='tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2'>
              <SearchIcon size={14} />
            </div>
            <input
              className='tw-border-gray-200 focus:tw-border-black tw-w-full tw-rounded-full tw-border tw-bg-transparent tw-py-3 tw-pl-10 tw-pr-4 tw-transition-colors placeholder:tw-text-gray-500 focus:tw-outline-none tw-type-sm'
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder || ""}
              type='text'
              value={searchQuery}
            />
          </div>
        </div>

        <div className='tw-overflow-x-auto'>
          <ListTable
            header={
              <div className='tw-grid tw-grid-cols-2 tw-items-center tw-gap-4 tw-py-4'>
                <div className='tw-pb-1.5'>
                  <p className='tw-type-base tw-font-medium tw-text-gray-500'>{t("icon_name")}</p>
                </div>
                <div />
              </div>
            }
          >
            {filteredVoices.length > 0 ? (
              filteredVoices.map(voice => (
                <ListTableRow key={voice.id}>
                  <VoiceTableRow voice={voice} />
                </ListTableRow>
              ))
            ) : (
              <li className='tw-py-12 tw-text-center'>
                <p className='tw-text-gray-500 tw-type-xs'>{t("no_voices_found")}</p>
              </li>
            )}
          </ListTable>
        </div>

        <div className='tw-mt-10'>
          <h4 className='tw-type-base tw-text-black'>{t("request_another_voice_title")}</h4>
          <p className='tw-mt-4 tw-mb-5 tw-text-gray-500 tw-type-xs tw-max-w-md'>
            {t("request_another_voice_description")}
          </p>
          <Button size='lg' variant='elevated' onClick={() => setIsDialogOpen(true)}>
            {t("request_voice_button")}
          </Button>
        </div>

        <RequestVoiceDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
      </div>
    </Block>
  );
};

const IconicVoicesTable = (props: IconicVoicesTableProps) => {
  return (
    <AudioProvider>
      <IconicVoicesTableLayout {...props} />
    </AudioProvider>
  );
};

IconicVoicesTable.displayName = "IconicVoicesTable";

export { IconicVoicesTable };
